/**
 * rest_client（旧 REST 接口客户端）—— OFBiz 迁移版
 * ------------------------------------------------------------------
 * 原实现通过 $.ajax 调 /rest/* 旧接口（URL 拼 token 鉴权）。
 * 旧接口下线后会统一返回 "token invalid."，导致前端误判登录失效。
 *
 * 现按 doc/20-前端接口对照与迁移清单 做如下处理，保持各方法签名
 * (params, callback, errorCallback) 不变，调用方无需改动：
 *
 *   1. 有明确 OFBiz 新接口的 → 委托 src/app/server/ofbiz 适配层，
 *      把 Promise 还原成旧的 callback 回调语义；
 *   2. 无新接口的（数据库管理/背景配置/License 认证/报表手动生成等，
 *      见 doc/20 第四节及 OFBiz 迁移交接文档第 5 节）→ 降级处理：
 *      不调旧接口，直接回调空数据，避免 "token invalid." 误登出。
 */
import { ofbiz as http } from './ofbiz/http';
import * as api from './ofbiz';

// 把 Promise 还原成 (callback, errorCallback) 回调语义的辅助函数
function thenCB(promise, callback, errorCallback) {
  return promise.then(
    (data) => { callback && callback(data); return data; },
    (err) => { errorCallback && errorCallback(err); return err; },
  );
}

// 降级辅助：无新接口时不调旧接口，直接回调空数据（仅用于"读"类，
// 调用方一般只 dispatch(data.xxx)，需保证字段存在非 undefined）
function degrade(callback, data) {
  callback && callback(data);
}

// 降级辅助：走错误回调（用于"写"类接口，避免误报成功）
// 传 errmsg 时会触发调用方内部的错误提示分支
function degradeFail(callback, errorCallback, errmsg) {
  const status = errmsg ? { errmsg } : { errmsg: '该接口暂未对接后端' };
  errorCallback && errorCallback(status);
}

class rest_client {
  // ==================== 微信/数据通讯（无新接口，降级） ====================

  //获取数据通讯二维码
  getDataCommunicationQrcode(callback, errorCallback) {
    // 无 OFBiz 对应接口，降级返回空
    degrade(callback, '');
  }

  //操作私有用户 开始 停止
  handlePrivateUser(params, callback, errorCallback) {
    // 无 OFBiz 对应接口（mqtt 控制），降级返回空
    degrade(callback, {});
  }

  // ==================== 告警事件 ====================

  getAlarmEvent(params, callback, errorCallback) {
    // /rest/api/v1/alarm/getAlarmEvents -> alarmEvent/list
    thenCB(
      api.getAlarmEventList(params || {}, (params && params.page) || 1, (params && params.pageSize) || 500),
      (data) => callback && callback({ results: (data && data.list) || [], total: (data && data.total) || 0 }),
      errorCallback,
    );
  }

  // ==================== 用户 / 认证 ====================

  //获取当前用户信息
  getUserInfo(callback, errorCallback) {
    // /rest/security/userinfo -> authMe
    thenCB(api.authMe(), callback, errorCallback);
  }

  //获取用户列表信息
  getUserList(callback, errorCallback) {
    // /rest/security/userinfos -> user/list
    api.getUserList({}, 1, 500).then(
      (res) => callback && callback({ users: (res && res.list) || [] }),
      (err) => errorCallback && errorCallback(err),
    );
  }

  //操作用户（增、删、改）
  operateUser(params, callback, errorCallback) {
    // /rest/security/userinfo/operate -> user/create | update | delete
    const op = params && (params.Operate || params.operate);
    let p;
    if (op === 'add') p = api.createUser(params);
    else if (op === 'edit') p = api.updateUser(params);
    else if (op === 'delete') p = api.deleteUser(params.partyId || params.PartyId);
    else p = api.updateUser(params);
    thenCB(p, callback, errorCallback);
  }

  //修改用户密码
  changePassword(params, callback, errorCallback) {
    // /rest/security/pwd/operate/update -> userChangePassword
    thenCB(
      api.userChangePassword(params.oldPassword || params.OldPassword, params.newPassword || params.NewPassword),
      callback,
      errorCallback,
    );
  }

  //重置用户密码
  resetPassword(params, callback, errorCallback) {
    // /rest/security/pwd/operate/reset -> user/resetPassword
    thenCB(
      api.resetPassword(params.userLoginId || params.LoginID, params.newPassword || params.NewPassword, params.requirePasswordChange),
      callback,
      errorCallback,
    );
  }

  //获取用户列表信息（个人资料下拉用）
  getProfileData(callback, errorCallback) {
    // /rest/security/userinfos -> user/list
    api.getUserList({}, 1, 500).then(
      (res) => {
        const users = (res && res.list) || [];
        callback && callback(users);
      },
      (err) => errorCallback && errorCallback([]),
    );
  }

  // ==================== 角色 / 权限 ====================

  //获取安全群组下的角色，不带 OrganizationName 时为获取所有角色
  getOrganizationRoleData(organizationName, callback, errorCallback) {
    // /rest/security/role/names -> role/list
    api.getRoleList({}, 1, 500).then(
      (res) => callback && callback({ results: (res && res.list) || [] }),
      (err) => errorCallback && errorCallback(err),
    );
  }

  //获取权限数据（所有可分配权限项）
  getPermissionData(roleName, callback, errorCallback) {
    // /rest/security/permission/list -> role/permissions
    thenCB(api.getPermissionList(), callback, errorCallback);
  }

  //获取角色权限（当前用户的角色权限）
  getRolePermission(roleName, callback, errorCallback) {
    // /rest/security/permission/get -> authMe（当前用户权限随登录返回）
    api.authMe().then(
      (info) => {
        const perms = (info && info.permissions) || [];
        callback && callback({
          permission: perms.map((item) => {
            const t = typeof item === 'string' ? item : (item.Type || item.permissionId || item.name || '');
            return { Type: t };
          }),
        });
      },
      (err) => errorCallback && errorCallback(err),
    );
  }

  //操作角色（增、改）
  operateRole(params, callback, errorCallback) {
    // /rest/security/role/operate -> role/create | update
    const op = params && (params.Operate || params.operate);
    const p = op === 'add' ? api.createRole(params) : api.updateRole(params);
    thenCB(p, callback, errorCallback);
  }

  //删除角色
  deleteRole(params, callback, errorCallback) {
    // /rest/security/role/delete -> role/delete
    thenCB(api.deleteRole(params.RoleId || params.roleId), callback, errorCallback);
  }

  //编辑角色权限
  updatePermission(params, callback, errorCallback) {
    // /rest/security/permission/editrole -> role/assignPermissions
    const ids = params.permissionIds || params.Permissions || params.permissionId;
    thenCB(api.assignPermissions(params.RoleId || params.roleId, ids), callback, errorCallback);
  }

  // ==================== 安全群组 / 运维团队 ====================

  //获取安全群组数据
  getGroupList(callback, errorCallback) {
    // /rest/security/safegroup/list -> safeGroup/list
    api.getSafeGroupList().then(
      (list) => callback && callback({ groups: list || [] }),
      (err) => errorCallback && errorCallback(err),
    );
  }

  //操作安全群组（增、改）
  operateGroup(params, callback, errorCallback) {
    // /rest/security/safegroup/operate -> safeGroup/create（旧接口无独立 update，暂走 create）
    api.createSafeGroup(params.OrganizationName, params.Description || params.OrganizationName).then(
      () => callback && callback({ code: 0 }),
      (err) => errorCallback && errorCallback(err),
    );
  }

  //删除安全群组
  deleteGroup(organizationName, callback, errorCallback) {
    // /rest/security/safegroup/delete -> safeGroup/delete
    api.deleteSafeGroup(organizationName).then(
      () => callback && callback({ code: 0 }),
      (err) => errorCallback && errorCallback(err),
    );
  }

  // ==================== 数据管理（无新接口，降级） ====================
  // doc/20 差距 #5：数据库备份/ES 清理由 OFBiz JobSandbox 承担，不暴露业务接口。

  //获取数据连接的配置列表
  getDBConnectionList(callback, errorCallback) {
    // 消费方 dispatch(data.displaynames)
    degrade(callback, { displaynames: [] });
  }

  //获取连接的对应参数详情
  getDBConnectionInfo(displayname, callback, errorCallback) {
    // 消费方 dispatch(data)，空对象即可
    degrade(callback, {});
  }

  //操作数据配置
  operateDBConnection(params, callback, errorCallback) {
    degradeFail(callback, errorCallback, '数据库连接操作接口暂未对接');
  }

  //数据库配置列表排序
  sortDBConnectionList(params, callback, errorCallback) {
    degradeFail(callback, errorCallback, '数据库连接排序接口暂未对接');
  }

  //测试数据连接
  testDBConnection(params, callback, errorCallback) {
    degradeFail(callback, errorCallback, '数据库连接测试接口暂未对接');
  }

  //获取数据库文件默认地址
  getDBDefaultBackDbUrl(callback, errorCallback) {
    // 消费方 dispatch(data.url)
    degrade(callback, { url: '' });
  }

  //更新数据库
  updateDatabase(params, callback, errorCallback) {
    degradeFail(callback, errorCallback, '数据库更新接口暂未对接');
  }

  //备份数据库
  backupDatabase(backdburl, callback, errorCallback) {
    degradeFail(callback, errorCallback, '数据库备份接口暂未对接');
  }

  //定时备份数据库
  backupDatabaseTiming(params, callback, errorCallback) {
    degradeFail(callback, errorCallback, '数据库定时备份接口暂未对接');
  }

  //开启数据日志存入 mysql
  syncLogToDatabase(issyn, callback, errorCallback) {
    degradeFail(callback, errorCallback, '同步日志到数据库接口暂未对接');
  }

  //手动删除 ES 数据
  deleteESManually(params, callback, errorCallback) {
    degradeFail(callback, errorCallback, 'ES 数据清除接口暂未对接');
  }

  //定时删除 ES 数据
  deleteESTiming(params, callback, errorCallback) {
    degradeFail(callback, errorCallback, 'ES 定时删除设置接口暂未对接');
  }

  //定时关闭 ES 索引
  closeESIndexTiming(params, callback, errorCallback) {
    degradeFail(callback, errorCallback, 'ES 索引定时关闭设置接口暂未对接');
  }

  // ==================== 系统运行时-背景配置（无文档，降级） ====================

  //获取系统运行时配置
  getBackground(callback, errorCallback) {
    // 消费方 dispatch(data)，空对象即可
    degrade(callback, {});
  }

  //操作系统运行时配置
  operateBackground(params, callback, errorCallback) {
    degradeFail(callback, errorCallback, '系统运行时配置操作接口暂未对接');
  }

  //保存系统运行时配置
  saveBackground(params, callback, errorCallback) {
    degradeFail(callback, errorCallback, '系统运行时配置保存接口暂未对接');
  }

  //获取系统运行服务列表
  getBackgroundServices(callback, errorCallback) {
    // 消费方 dispatch(data.names)
    degrade(callback, { names: [] });
  }

  // ==================== 系统（selfcheck / 时间 / 调度 / license） ====================

  //获取系统自检数据
  getSelfCheckData(callback, errorCallback) {
    // /rest/system/selfcheck -> system/selfcheck
    thenCB(api.getSelfCheck(), callback, errorCallback);
  }

  //获取服务器当前时间
  getServerCurrentTime(callback, errorCallback) {
    // /rest/other/currenttime -> system/currentTime
    thenCB(api.getCurrentTime(), callback, errorCallback);
  }

  //获取调度信息
  getSchedulerData(callback, errorCallback) {
    // /rest/other/scheduler/data -> system/scheduler
    api.getScheduler().then(
      (data) => {
        const names = (data && (data.names || data.list)) || [];
        callback && callback({ names });
      },
      (err) => errorCallback && errorCallback(err),
    );
  }

  //获取软件许可数据
  getLicenseInfoData(callback, errorCallback) {
    // /rest/system/license/info -> system/licenseInfo
    thenCB(api.getLicenseInfo(), callback, errorCallback);
  }

  // ==================== 网络拓扑 ====================

  //获取视图节点数据
  getNetworkNodes(type, callback, errorCallback) {
    // /rest/view/nodeinfo -> topo/nodes
    http.post('topo/nodes', null, { type }).then(
      (data) => {
        const list = (data && (data.NetWorkNodes || data.list)) || (Array.isArray(data) ? data : []);
        callback && callback({ NetWorkNodes: list });
      },
      (err) => errorCallback && errorCallback(err),
    );
  }

  //获取视图节点连线数据
  getNetworkNodeLine(type, callback, errorCallback) {
    // /rest/view/nodeline -> topo/edges
    http.post('topo/edges', null, { type }).then(
      (data) => {
        const lines = (data && (data.lines || data.list)) || (Array.isArray(data) ? data : []);
        callback && callback({ lines });
      },
      (err) => errorCallback && errorCallback(err),
    );
  }

  // ==================== 通知 ====================

  //测试发送接口（邮件 短信 微信）
  testSend(params, callback, errorCallback) {
    // /rest/alarm/send -> notify/testSend
    thenCB(http.post('notify/testSend', params), callback, errorCallback);
  }

  //获取发送日志数据
  getSendLogs(params, callback, errorCallback) {
    // /rest/alarm/sendlog -> notify/sendLogs
    const query = {};
    ['startTime', 'endTime'].forEach((k) => { if (params && params.hasOwnProperty(k)) query[k] = params[k]; });
    if (params && params.EquipmentId) query.equipmentId = params.EquipmentId;
    if (params && params.AlarmName) query.alarmName = params.AlarmName;
    const page = params && params.fromindex ? Math.floor(params.fromindex / (params.pagesize || 20)) + 1 : 1;
    const pageSize = (params && params.pagesize) || 20;
    http.postPage('notify/sendLogs', query, page, pageSize).then(
      (res) => callback && callback({ code: 0, details: (res && res.list) || [], num: (res && res.total) || 0 }),
      (err) => errorCallback && errorCallback(err),
    );
  }

  // ==================== 报表手动生成/导出（差距 #7，降级） ====================

  //手动生成报表
  createReportManual(params, callback, errorCallback) {
    // 无对应"按需生成报表文件"接口（report/create 是建报表配置），降级
    degrade(callback, { code: 0 });
  }

  //导出生成的报表
  exportReport(params, callback, errorCallback) {
    // 无对应接口，降级走错误回调
    errorCallback && errorCallback({ errmsg: '报表导出接口暂未对接' });
  }

  // ==================== 资源图标上传（无接口，降级） ====================

  //上传资源
  uploadResource(formData, callback, errorCallback) {
    // 资源图标库无 OFBiz 接口（已改用本地静态图标，见 setting_action.getImageResource）
    degrade(callback, {});
  }

  // ==================== 认证 URL / License 绑定（无接口，降级） ====================
  // doc/20 差距 #5：License/认证绑定属系统运维级，新后端仅提供只读 licenseInfo。

  //获取认证 URL
  getAuthenticationData(callback, errorCallback) {
    degrade(callback, {});
  }

  //更新认证 URL
  updateAuthenticationUrl(url, callback, errorCallback) {
    degrade(callback, { code: 0 });
  }

  //绑定认证 IP
  bindAuthenticationIP(callback, errorCallback) {
    degrade(callback, { code: 0 });
  }

  //解绑认证 IP
  unbindAuthenticationIP(callback, errorCallback) {
    degrade(callback, { code: 0 });
  }

  //获取 license 列表
  getLicenseList(callback, errorCallback) {
    degrade(callback, []);
  }

  //绑定 license
  bindLicense(license, callback, errorCallback) {
    degrade(callback, { code: 0 });
  }

  //解绑 license
  unbindLicense(callback, errorCallback) {
    degrade(callback, { code: 0 });
  }

  // ==================== 注销 ====================

  //注销
  logout(callback, errorCallback) {
    // /rest/security/logout -> logout
    api.logout().then(
      () => callback && callback({ code: 0 }),
      () => callback && callback({ code: 0 }), // 即使退出接口失败也放行前端登出
    );
  }

}

const RestClient = new rest_client();
export default RestClient;
